import React from 'react';
import {
  Shield, Activity, TrendingUp, BarChart3,
  Zap, Globe, Wind, Sun,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const RenewableTechnologyMonitoringPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Renewable Technology Asset Performance Monitoring',
      title: (
        <>
          Monitor and assess the health <em>of every renewable technology</em>
        </>
      ),
      lede: 'Comprehensive performance monitoring solution for renewable technology assets, providing a unified view and highlighting which assets need technical attention across all renewable energy technologies.',
      image: '/assets/img/general/steptodown.com430799.jpg',
      imageAlt: 'Mixed renewable energy generation assets',
      actions: [
        { label: 'Start monitoring', to: '/contact' },
        { label: 'Learn more', to: '/case-studies' },
      ],
      metrics: [
        { value: '99.9%', label: 'Uptime monitoring' },
        { value: '24/7', label: 'Real-time alerts' },
        { value: '500+', label: 'Assets monitored' },
        { value: '<1min', label: 'Response time' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Unified technology monitoring',
      lede: 'Our comprehensive monitoring solution provides a unified view of all your renewable technology assets, enabling you to identify and address issues across different technologies from a single platform.',
      checklist: [
        'Monitor multiple renewable technologies',
        'Reduce maintenance costs by 30%',
        'Increase overall efficiency by 20%',
        'Improve asset lifespan by 25%',
      ],
      image: '/assets/img/general/pinwheels-6535595_1280.jpg',
      imageAlt: 'Wind assets alongside other renewable technologies',
      imageCaption: 'Solar, wind, storage and hybrid assets in one operational view',
    }}
    capabilities={{
      eyebrow: 'Key features & benefits',
      title: 'Monitoring built for mixed portfolios',
      lede: 'Comprehensive monitoring designed to maximize renewable technology performance.',
      items: [
        {
          icon: Activity,
          title: 'Real-time monitoring',
          description: 'Continuous monitoring of renewable technology assets with instant alerts and notifications.',
        },
        {
          icon: BarChart3,
          title: 'Advanced analytics',
          description: 'Comprehensive data analysis with predictive insights and trend identification.',
        },
        {
          icon: Shield,
          title: 'Asset health assessment',
          description: 'Proactive identification of potential issues before they impact performance.',
        },
        {
          icon: TrendingUp,
          title: 'Performance optimization',
          description: 'AI-driven recommendations to maximize efficiency and output across all renewable technologies.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Supported renewable technologies',
      title: 'One platform, every generation type',
      lede: 'Comprehensive monitoring solutions for all types of renewable energy technologies.',
      items: [
        {
          icon: Sun,
          title: 'Solar technology',
          description: 'Advanced monitoring for photovoltaic systems and solar farms.',
        },
        {
          icon: Wind,
          title: 'Wind technology',
          description: 'Comprehensive monitoring for wind turbines and wind farms.',
        },
        {
          icon: Zap,
          title: 'Energy storage',
          description: 'Battery and storage system performance monitoring.',
        },
        {
          icon: Globe,
          title: 'Hybrid systems',
          description: 'Integrated monitoring for combined renewable energy systems.',
        },
      ],
    }}
    cta={{
      title: 'Ready to monitor your renewable technologies?',
      body: 'Discover how our comprehensive monitoring solution can optimize your renewable technology portfolio.',
      primary: { label: 'Start monitoring', to: '/contact' },
      secondary: { label: 'Learn more', to: '/case-studies' },
    }}
  />
);

export default RenewableTechnologyMonitoringPage;
